<script setup>
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import ConversationBox from 'dashboard/components/widgets/conversation/ConversationBox.vue';
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  displayId: {
    type: [Number, String],
    required: true,
  },
  inboxId: {
    type: Number,
    default: 0,
  },
  contactName: {
    type: String,
    default: '',
  },
  conversationHref: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const router = useRouter();
const currentChat = useMapGetter('getSelectedChat');
const isSidebarOpen = ref(false);

watch(
  () => props.show,
  val => {
    if (!val) isSidebarOpen.value = false;
  }
);

function close() {
  emit('close');
}

function toggleSidebar() {
  isSidebarOpen.value = !isSidebarOpen.value;
}

function navigate(event) {
  event.stopPropagation();
  if (props.conversationHref) router.push(props.conversationHref);
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="contact-conversation-dock fixed right-4 bottom-4 z-[9000] w-[980px] max-w-[calc(100vw-1.5rem)] h-[78vh] max-h-[860px] rounded-xl border border-n-weak bg-n-solid-1 shadow-2xl flex flex-col overflow-visible"
    >
      <div
        class="relative z-[220] h-12 px-3 border-b border-n-weak flex items-center justify-between gap-2 flex-shrink-0"
      >
        <p class="text-sm font-semibold text-n-slate-12 truncate min-w-0">
          #{{ displayId }}
          <span
            v-if="contactName"
            class="ml-1 text-xs font-normal text-n-slate-10"
          >
            · {{ contactName }}
          </span>
        </p>
        <div class="inline-flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            class="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-n-alpha-2 text-n-slate-11"
            :title="t('INTERNAL_CHAT.DOCK.TOGGLE_CONTACT')"
            @click="toggleSidebar"
          >
            <span class="i-ph-user-bold size-4" />
          </button>
          <button
            v-if="conversationHref"
            type="button"
            class="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-n-alpha-2 text-n-slate-11"
            :title="t('INTERNAL_CHAT.CONVERSATION_MENTION.OPEN_CONVERSATION')"
            @click="navigate"
          >
            <Icon icon="i-lucide-external-link" class="size-4" />
          </button>
          <button
            type="button"
            class="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-n-alpha-2 text-n-slate-11"
            @click="close"
          >
            <Icon icon="i-lucide-x" class="size-4" />
          </button>
        </div>
      </div>

      <div class="flex-1 min-h-0 h-full overflow-visible">
        <section class="flex w-full h-full min-w-0 overflow-visible">
          <ConversationBox
            class="h-full min-w-0 flex-1"
            :inbox-id="inboxId"
            is-inbox-view
            :is-on-expanded-layout="false"
          />
          <aside
            v-if="isSidebarOpen && currentChat?.id"
            class="w-[340px] min-w-[340px] h-full border-l border-n-weak bg-n-surface-2 overflow-auto"
          >
            <ContactPanel
              :conversation-id="currentChat.id"
              :inbox-id="currentChat.inbox_id"
              :fetch-contact-details="false"
              :show-header="false"
              hide-deals-section
            />
          </aside>
        </section>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss">
.contact-conversation-dock {
  :deep(.conversation-header .actions--container) {
    position: relative;
    z-index: 120;
  }

  :deep(.conversation-header .actions--container [class*='top-full']) {
    left: auto !important;
    right: calc(100% + 0.25rem) !important;
    top: 100% !important;
    z-index: 200 !important;
  }
}
</style>
